import { Directive, Input, OnInit, OnChanges, ElementRef } from '@angular/core';
import { CVLocalizationService } from './services/cv-localization.service';

@Directive({
    selector: '[cvLocalizedAttrs]',
})
export class CvLocalizedAttrsDirective implements OnInit, OnChanges {

    /** String of one or more comma-separated attributes. Spaces are not allowed in attribute names. Use cvLocalizedText for the text of the element. */
    @Input()
    cvLocalizedAttrs: string;

    /** Localization section name. Defaults to reading [langSection] from cvLocalizedText if this is not provided. */
    @Input()
    attrLangSection: string;

    /** Localization key name. Defaults to reading [langKey] from cvLocalizedText if this is not provided. */
    @Input()
    attrLangKey: string;

    /** Localization section name. */ // Fallback
    @Input()
    langSection: string;

    /** Localization key name. */ // Fallback
    @Input()
    langKey: string;

    // Map of attribute name to text value.  Used as the default when the above do not point to a valid string.
    // Kept in this map in case the bindings above change later to something invalid
    private defaultValues = new Map<string, string>()

    // Used to track which localization is occurring, in case the any attributes change before the network request returns.
    private lastProcessed: string;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private element: ElementRef<HTMLElement>,
    ) { }

    ngOnInit(): void {
        if (typeof this.cvLocalizedAttrs !== "string") {
            console.error("CvLocalizedAttrs requires the attribute names to localize.");
            return;
        }

        this.processAttributes();
    }

    ngOnChanges(): void {
        this.processAttributes();
    }

    async processAttributes() {
        const processKey = this.getProcessedKey();
        if (this.lastProcessed == processKey)
            return;

        this.lastProcessed = processKey;

        const attributes = this.cvLocalizedAttrs?.split(",").filter(a => a.length);
        if (!attributes || !attributes.length) {
            console.warn("CvLocalizedAttrs: no attributes to localize specified");
            return;
        }

        const allValid = attributes.every(a => { 
            try {
                this.element.nativeElement.getAttribute(a);
                return true;
            } catch (ex) {
                console.error(`CvLocalizedAttr: exception with attribute name "${a}"`);
                return false;
            }
        });

        if (!allValid)
            return;

        const langSection = this.attrLangSection ?? this.langSection;
        const langKey = this.attrLangKey ?? this.langKey;

        if (!langSection || !langKey) {
            console.error("CvLocalizedAttrs: missing section or key");
            return;
        }

        const localizedString = await this.cvLocalizationService.GetLocalText(langSection, langKey, "");

        if (this.lastProcessed != processKey)
            return; // Stop processing this promise, since this.lastProcessed has changed since this method was called.

        for (const attr of attributes) {
            if (this.defaultValues.has(attr) && !localizedString) {
                this.element.nativeElement.setAttribute(attr, this.defaultValues.get(attr));
            } else if (localizedString) {
                // If the default value has not been remembered before, save it here before it's replaced.
                if (!this.defaultValues.has(attr)) {
                    const defaultAttrValue = this.element.nativeElement.getAttribute(attr);
                    this.defaultValues.set(attr, defaultAttrValue);
                }
                this.element.nativeElement.setAttribute(attr, localizedString);
            }
        }
    }

    private getProcessedKey(): string {
        const langSection = this.attrLangSection ?? this.langSection;
        const langKey = this.attrLangKey ?? this.langKey;

        return `${this.cvLocalizedAttrs}_${langSection}_${langKey}`;
    }
}
